import { NavigateFunction } from "react-router-dom"

import { ProjectData } from "types/project.types"

/** The project list's views, as the header switch shows them. */
export type ProjectView = "owned" | "member" | "all" | "archived"

export interface ProjectListState {
  view: ProjectView
  openFolders: string[]
}

const STORAGE_KEY = "dq.projectList.v1"

const VIEWS: readonly ProjectView[] = ["owned", "member", "all", "archived"]

const defaultState = (): ProjectListState => ({ view: "owned", openFolders: [] })

const isView = (value: unknown): value is ProjectView =>
  typeof value === "string" && (VIEWS as readonly string[]).includes(value)

/** "All" is an admin view; anyone else falls back to Owned. */
export const normaliseView = (view: ProjectView, isAdmin: boolean): ProjectView =>
  view === "all" && !isAdmin ? "owned" : view

/**
 * The list's remembered view and open folders (per browser). Empty, broken or
 * unreadable storage gives the defaults: Owned, nothing open.
 */
export const loadListState = (): ProjectListState => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultState()
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return defaultState()
    const { view, openFolders } = parsed as Record<string, unknown>
    return {
      view: isView(view) ? view : "owned",
      openFolders: Array.isArray(openFolders)
        ? openFolders.filter((f): f is string => typeof f === "string")
        : [],
    }
  } catch {
    return defaultState()
  }
}

/** Saves the list's view and open folders; storage errors are ignored. */
export const saveListState = (state: ProjectListState): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ view: state.view, openFolders: state.openFolders })
    )
  } catch {
    // Private window or blocked storage: the list just won't remember.
  }
}

/**
 * The view that lists this project: Archived when it is archived, Owned when
 * the user owns it, otherwise Member. The project lists carry no archived
 * marker, so `archived` is set by whoever knows (e.g. the archived list).
 */
export const viewFor = (
  project: Pick<ProjectData, "owner" | "archived">,
  userId: string
): ProjectView => {
  if (project.archived) return "archived"
  return project.owner.toLowerCase() === userId.toLowerCase()
    ? "owned"
    : "member"
}

/** Router state that opens the project list at a folder, in a given view. */
export interface RevealFolderState {
  revealFolder: string
  view: ProjectView
  /**
   * With view "all": the view that lists the project itself, used instead if
   * "All" turns out not to be open to the user.
   */
  ownView?: ProjectView
}

/** True if router state asks the list to open at a folder. */
export const isRevealFolderState = (
  state: unknown
): state is RevealFolderState => {
  if (!state || typeof state !== "object") return false
  const { revealFolder, view, ownView } = state as Record<string, unknown>
  return (
    typeof revealFolder === "string" &&
    isView(view) &&
    (ownView === undefined || isView(ownView))
  )
}

/** Where to open the project list: a folder, in a view. */
export interface ListTarget {
  folder: string
  view: ProjectView
  /** With view "all": the project's own view (see RevealFolderState). */
  ownView?: ProjectView
}

/**
 * Opens the project list at a folder ("" for the top), in the given view: the
 * list opens the folder and its parents, scrolls to it and highlights it.
 */
export const openFolderInList = (
  navigate: NavigateFunction,
  target: ListTarget
): void => {
  const state: RevealFolderState = {
    revealFolder: target.folder,
    view: target.view,
    ...(target.ownView ? { ownView: target.ownView } : {}),
  }
  navigate("/home", { state })
}

/**
 * Where the list shows the open project (from the project store): its folder
 * and the view that lists it. The store has no archived marker, so an
 * archived project opens in Owned or Member.
 *
 * A list left on "All" stays on All (it lists every project), so an admin
 * isn't moved to Owned or Member; only an admin can pick All, and anyone else
 * still gets the project's own view (ownView).
 */
export const listTargetFor = (
  project: { Owner: string; Folder?: string },
  userId: string
): ListTarget => {
  const folder = project.Folder?.trim() ?? ""
  const ownView = viewFor({ owner: project.Owner ?? "" }, userId)
  return loadListState().view === "all" && ownView !== "archived"
    ? { folder, view: "all", ownView }
    : { folder, view: ownView }
}
